import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, serverTimestamp, onSnapshot, query, where } from 'firebase/firestore';
import { db, auth } from '../firebase';
import TimeSlotPicker from './timeslotpicker';
import BarberUpload from './barber-upload'; 
import toast from 'react-hot-toast';

const NAVY = '#001F3F';
const GOLD = '#B8860B';

export default function BarberDetail() {
  const { barberId } = useParams();
  const navigate = useNavigate();
  
  const [barber, setBarber] = useState(null);
  const [userStyles, setUserStyles] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userHeadScan, setUserHeadScan] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  const user = auth.currentUser;

  const toastStyle = {
    style: {
      background: NAVY,
      color: GOLD,
      border: `1px solid ${GOLD}`,
      fontSize: '0.85rem',
      borderRadius: '12px',
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch Barber Info
        const barberSnap = await getDoc(doc(db, 'barbers', barberId));
        if (barberSnap.exists()) {
          setBarber(barberSnap.data());
        }

        // Fetch User Scan Data
        if (user) {
          const userSnap = await getDoc(doc(db, 'users', user.uid));
          if (userSnap.exists()) {
            setUserHeadScan(userSnap.data()?.headScan);
          }
        }
      } catch (err) {
        console.error("Initialization Error:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserVault = () => {
      if (!user) return;
      // We listen to 'user_styles' (the collection where GlobalGallery saves items)
      const q = query(collection(db, 'user_styles'), where('userId', '==', user.uid));
      return onSnapshot(q, (snap) => {
        setUserStyles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
    };

    fetchInitialData();
    const unsubVault = fetchUserVault();
    return () => unsubVault?.();
  }, [barberId, user]);

  const handleFinalBooking = async () => {
    if (!user) {
      toast.error("Please login to book", toastStyle);
      navigate('/login');
      return;
    }
    if (!selectedSlot || !selectedStyle) {
      toast.error("Please select a Time and a Style Architecture", toastStyle);
      return;
    }

    const bookingToast = toast.loading("Syncing 3D Consultation...", toastStyle);

    try {
      await addDoc(collection(db, 'bookings'), {
        barberId,
        barberName: barber.shopName || barber.name,
        userId: user.uid,
        userName: user.displayName || 'Esdras Client',
        appointmentTime: selectedSlot,
        styleId: selectedStyle.styleId || selectedStyle.id,
        styleName: selectedStyle.name,
        styleImage: selectedStyle.image,
        userHeadScan: userHeadScan || null, // The 3D head mesh of the user
        status: 'pending',
        escrowStatus: 'held',
        createdAt: serverTimestamp()
      });

      toast.success("Booking & 3D Preview Sent!", { id: bookingToast, ...toastStyle });
      // Redirect to a summary or dashboard
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      console.error(err);
      toast.error("Booking failed. Please check connection.", { id: bookingToast, ...toastStyle });
    }
  };

  if (loading) return <div style={loader}>CALIBRATING BOOKING ENGINE...</div>;

  return (
    <div style={container}>
      <div style={header}>
        <button onClick={() => navigate(-1)} style={backBtn}>←</button>
        <h2 style={{ color: GOLD, margin: 0, fontSize: '1rem', letterSpacing: '1px' }}>
          {barber?.shopName?.toUpperCase() || 'STYLIST PROFILE'}
        </h2>
      </div>

      <div style={content}>
        {/* BARBER INFO CARD */}
        <div style={infoCard}>
          <div style={imageBadgeContainer}>
            <img src={barber?.image || '/icons/default-avatar.png'} style={profileImg} alt="stylist" />
            <div style={verifiedBadge}>VERIFIED</div>
          </div>
          <h3 style={{ margin: '15px 0 5px', fontSize: '1.4rem' }}>{barber?.name}</h3>
          <p style={{ color: GOLD, margin: 0, fontSize: '0.9rem', fontWeight: 'bold' }}>
            ★ {barber?.averageRating || '5.0'} • {barber?.specialty || 'Master Stylist'}
          </p>
          <p style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '8px' }}>{barber?.address}</p>
          
          {user?.uid === barberId && (
            <button 
              onClick={() => setShowUpload(true)}
              style={uploadTriggerBtn}
            >
              + MANAGE PORTFOLIO
            </button>
          )}
        </div>

        {/* STEP 1: STYLE VAULT SELECTION */}
        <div style={sectionHeader}>
            <h3 style={sectionLabel}>1. ATTACH 3D STYLE ARCHITECTURE</h3>
            <span style={vaultCount}>{userStyles.length} SAVED</span>
        </div>
        
        <div style={styleGrid}>
          {userStyles.length === 0 ? (
            <div style={emptyVaultCard} onClick={() => navigate('/gallery')}>
                <p style={{ margin: 0 }}>Vault Empty</p>
                <span style={{ fontSize: '0.6rem', color: GOLD }}>BROWSE GALLERY →</span>
            </div>
          ) : (
            userStyles.map(style => (
              <div 
                key={style.id} 
                onClick={() => setSelectedStyle(style)}
                style={{
                  ...styleCard, 
                  border: selectedStyle?.id === style.id ? `2px solid ${GOLD}` : '2px solid transparent',
                  background: selectedStyle?.id === style.id ? 'rgba(184,134,11,0.1)' : 'rgba(255,255,255,0.05)'
                }}
              >
                <img src={style.image} style={styleImg} alt="3d-style" />
                <div style={styleCardMeta}>
                    <p style={styleCardTitle}>{style.name}</p>
                </div>
              </div>
            ))
          )}
        </div>

        {/* STEP 2: TIME PICKER */}
        <h3 style={sectionLabel}>2. SELECT APPOINTMENT WINDOW</h3>
        <div style={pickerWrapper}>
          <TimeSlotPicker stylistId={barberId} onSlotSelected={setSelectedSlot} />
        </div>

        {/* FINAL ACTION */}
        <div style={stickyFooter}>
            <button 
                onClick={handleFinalBooking} 
                disabled={!selectedSlot || !selectedStyle}
                style={{
                    ...confirmBtn, 
                    background: (selectedSlot && selectedStyle) ? GOLD : '#333',
                    color: (selectedSlot && selectedStyle) ? 'black' : '#666'
                }}
            >
                {(selectedSlot && selectedStyle) ? 'CONFIRM & SEND 3D PREVIEW' : 'COMPLETE STEPS 1 & 2'}
            </button>
        </div>
      </div>

      {showUpload && (
        <BarberUpload 
          barberData={barber} 
          onClose={() => setShowUpload(false)} 
        />
      )}
    </div>
  );
}

// --- ENHANCED STYLES ---
const container = { minHeight: '100vh', background: NAVY, color: 'white', paddingBottom: '100px', fontFamily: 'Montserrat, sans-serif' };
const header = { padding: '20px', display: 'flex', alignItems: 'center', gap: '20px', background: 'rgba(0,31,63,0.9)', backdropFilter: 'blur(10px)', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid rgba(184,134,11,0.2)' };
const backBtn = { background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' };
const content = { padding: '20px', maxWidth: '500px', margin: '0 auto' };
const loader = { height: '100vh', display: 'grid', placeItems: 'center', background: NAVY, color: GOLD, fontWeight: 'bold', letterSpacing: '2px' };

const infoCard = { textAlign: 'center', marginBottom: '30px', padding: '30px 20px', background: 'linear-gradient(145deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))', borderRadius: '30px', border: '1px solid rgba(184,134,11,0.1)' };
const imageBadgeContainer = { position: 'relative', width: '100px', margin: '0 auto' };
const profileImg = { width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: `2px solid ${GOLD}`, padding: '3px' };
const verifiedBadge = { position: 'absolute', bottom: '-5px', left: '50%', transform: 'translateX(-50%)', background: GOLD, color: 'black', fontSize: '0.5rem', fontWeight: '900', padding: '2px 8px', borderRadius: '10px' };

const uploadTriggerBtn = { background: 'none', border: `1px solid ${GOLD}`, color: GOLD, padding: '8px 20px', borderRadius: '50px', fontSize: '0.75rem', marginTop: '15px', cursor: 'pointer', fontWeight: 'bold' };

const sectionHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' };
const sectionLabel = { color: GOLD, fontSize: '0.7rem', letterSpacing: '2px', fontWeight: '800', margin: 0 };
const vaultCount = { fontSize: '0.6rem', opacity: 0.5 };

const styleGrid = { display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '10px', marginBottom: '25px', scrollSnapType: 'x mandatory' };
const emptyVaultCard = { minWidth: '140px', height: '120px', borderRadius: '20px', border: `1px dashed ${GOLD}44`, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', gap: '5px', fontSize: '0.8rem' };
const styleCard = { minWidth: '130px', borderRadius: '20px', overflow: 'hidden', textAlign: 'center', transition: '0.3s', scrollSnapAlign: 'start' };
const styleImg = { width: '100%', height: '130px', objectFit: 'cover' };
const styleCardMeta = { padding: '10px' };
const styleCardTitle = { fontSize: '0.65rem', margin: 0, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' };

const pickerWrapper = { background: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '25px', border: '1px solid rgba(255,255,255,0.05)' };
const stickyFooter = { position: 'fixed', bottom: 0, left: 0, right: 0, padding: '20px', background: 'rgba(0,31,63,0.95)', backdropFilter: 'blur(10px)', borderTop: '1px solid rgba(255,255,255,0.05)', zIndex: 20 };
const confirmBtn = { width: '100%', maxWidth: '460px', margin: '0 auto', display: 'block', padding: '18px', border: 'none', borderRadius: '50px', fontWeight: '900', fontSize: '0.9rem', letterSpacing: '1px', transition: '0.3s' };
              
